import React, { useState } from 'react'
import Header from '../components/Header'
import Footer from '../components/Footer';
import Newsletter from '../components/Newsletter';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faChevronRight,
    faEnvelope,
    faLock,
    faUser,
    faPhone,
    faEye,
    faEyeSlash,
    faArrowRight,
    faCheckCircle,
    faShieldAlt,
    faBolt,
    faHeadset,
    faSpinner,
    faExclamationCircle,
} from "@fortawesome/free-solid-svg-icons";
import { login, register, forgotPassword } from '../services/authService';

const PERKS = [
    { icon: faBolt,         text: "Fast order tracking & management" },
    { icon: faShieldAlt,    text: "Secure payments & data privacy"   },
    { icon: faHeadset,      text: "Priority support for members"     },
    { icon: faCheckCircle,  text: "Instant invoice downloads"        },
];

export default function LoginSignup() {
    const navigate = useNavigate();

    const [mode,        setMode]        = useState("login"); // "login" | "signup" | "forgot"
    const [showPass,    setShowPass]    = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [submitted,   setSubmitted]   = useState(false);   // forgot success state
    const [loading,     setLoading]     = useState(false);
    const [error,       setError]       = useState('');

    // ── Login form state ──
    const [loginData, setLoginData] = useState({ email: '', password: '' });

    // ── Signup form state ──
    const [signupData, setSignupData] = useState({
        first_name: '', last_name: '', email: '',
        phone: '', password: '', password_confirmation: '',
    });

    // ── Forgot form state ──
    const [forgotEmail, setForgotEmail] = useState('');

    const switchMode = (m) => {
        setError('');
        setSubmitted(false);
        setShowPass(false);
        setShowConfirm(false);
        setMode(m);
    };

    // ─── Handlers ──────────────────────────────────────────────────────────

    const handleLoginSubmit = async () => {
        setError('');
        if (!loginData.email || !loginData.password) {
            setError('Please enter your email and password.');
            return;
        }
        try {
            setLoading(true);
            await login(loginData.email, loginData.password);
            navigate('/user-dashboard');
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSignupSubmit = async () => {
        setError('');
        const { first_name, last_name, email, phone, password, password_confirmation } = signupData;

        if (!first_name || !last_name || !email || !password || !password_confirmation) {
            setError('Please fill in all required fields.');
            return;
        }
        if (password !== password_confirmation) {
            setError('Passwords do not match.');
            return;
        }
        if (password.length < 8) {
            setError('Password must be at least 8 characters.');
            return;
        }
        try {
            setLoading(true);
            await register({ first_name, last_name, email, phone, password, password_confirmation });
            // Auto-login happened inside register() — redirect straight to dashboard
            navigate('/user-dashboard');
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleForgotSubmit = async () => {
        setError('');
        if (!forgotEmail) {
            setError('Please enter your email address.');
            return;
        }
        try {
            setLoading(true);
            await forgotPassword(forgotEmail);
            setSubmitted(true);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // ─── Render ────────────────────────────────────────────────────────────
    return (
        <>
            <style>{`
                /* ══════════════════════════════════════════
                   SCOPED LOGIN/SIGNUP STYLES — prefix: lsup-
                ══════════════════════════════════════════ */

                .lsup-wrapper {
                    background: #f0f4f8;
                    min-height: 80vh;
                    padding: 56px 0 72px;
                }

                .lsup-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 0;
                    max-width: 980px;
                    margin: 0 auto;
                    border-radius: 22px;
                    overflow: hidden;
                    box-shadow: 0 8px 48px rgba(0,37,54,0.13);
                }

                /* ── Left panel ── */
                .lsup-panel-left {
                    background: linear-gradient(155deg, var(--primary-color) 0%, #1d6a88 60%, #00a8cc 100%);
                    padding: 52px 44px;
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                    position: relative;
                    overflow: hidden;
                }
                .lsup-panel-left::before {
                    content: "";
                    position: absolute;
                    top: -80px; right: -80px;
                    width: 260px; height: 260px;
                    border-radius: 50%;
                    background: rgba(255,255,255,0.06);
                }
                .lsup-panel-left::after {
                    content: "";
                    position: absolute;
                    bottom: -60px; left: -60px;
                    width: 200px; height: 200px;
                    border-radius: 50%;
                    background: rgba(255,255,255,0.04);
                }
                .lsup-brand { position: relative; z-index: 1; }
                .lsup-brand-logo {
                    font-size: 28px; font-weight: 800; color: #fff;
                    margin: 0 0 8px; letter-spacing: -0.5px; line-height: 1.2;
                }
                .lsup-brand-tagline {
                    font-size: 13px; color: rgba(255,255,255,0.65);
                    margin: 0; font-weight: 400; letter-spacing: 0.02em;
                }
                .lsup-left-body { position: relative; z-index: 1; }
                .lsup-left-heading {
                    font-size: 26px; font-weight: 800; color: #fff;
                    margin: 0 0 10px; line-height: 1.25;
                }
                .lsup-left-sub {
                    font-size: 14px; color: rgba(255,255,255,0.7);
                    margin: 0 0 32px; line-height: 1.6;
                }
                .lsup-perks { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 14px; }
                .lsup-perk { display: flex; align-items: center; gap: 12px; }
                .lsup-perk-icon {
                    width: 34px; height: 34px; border-radius: 9px;
                    background: rgba(255,255,255,0.12);
                    display: flex; align-items: center; justify-content: center;
                    font-size: 13px; color: #fff; flex-shrink: 0;
                    border: 1px solid rgba(255,255,255,0.15);
                }
                .lsup-perk-text { font-size: 13.5px; color: rgba(255,255,255,0.85); font-weight: 500; }
                .lsup-left-footer { position: relative; z-index: 1; }
                .lsup-left-footer p { font-size: 12px; color: rgba(255,255,255,0.4); margin: 0; }

                /* ── Right panel ── */
                .lsup-panel-right {
                    background: #ffffff;
                    padding: 52px 48px;
                    display: flex; flex-direction: column; justify-content: center;
                }

                /* ── Tabs ── */
                .lsup-tabs {
                    display: flex; background: #f0f4f8; border-radius: 12px;
                    padding: 4px; margin-bottom: 32px; gap: 4px;
                }
                .lsup-tab {
                    flex: 1; padding: 10px 12px; border: none; background: transparent;
                    border-radius: 9px; font-size: 14px; font-weight: 600;
                    color: #7a8fa3; cursor: pointer; transition: all 0.22s ease;
                }
                .lsup-tab.active {
                    background: var(--primary-color); color: #fff;
                    box-shadow: 0 3px 12px rgba(0,37,54,0.22);
                }
                .lsup-tab:hover:not(.active) { color: var(--primary-color); background: rgba(0,37,54,0.05); }

                /* ── Form ── */
                .lsup-form-title { font-size: 22px; font-weight: 800; color: var(--primary-color); margin: 0 0 4px; }
                .lsup-form-subtitle { font-size: 13.5px; color: #8a99ad; margin: 0 0 28px; }

                /* ── Error banner ── */
                .lsup-error-banner {
                    background: #fff1f0; border: 1.5px solid #ffc2be;
                    border-radius: 10px; padding: 11px 16px;
                    font-size: 13.5px; color: #c0392b; font-weight: 600;
                    margin-bottom: 18px;
                    display: flex; align-items: center; gap: 8px;
                }

                /* ── Divider ── */
                .lsup-divider {
                    display: flex; align-items: center; gap: 12px; margin-bottom: 24px;
                }
                .lsup-divider-line { flex: 1; height: 1.5px; background: #eef1f5; }
                .lsup-divider-text {
                    font-size: 12px; font-weight: 600; color: #b0bec8;
                    text-transform: uppercase; letter-spacing: 0.08em; white-space: nowrap;
                }

                /* ── Fields ── */
                .lsup-fields { display: flex; flex-direction: column; gap: 16px; margin-bottom: 20px; }
                .lsup-field-row { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
                .lsup-field { display: flex; flex-direction: column; gap: 6px; }
                .lsup-field label {
                    font-size: 12px; font-weight: 700; color: #6b7a90;
                    letter-spacing: 0.04em; text-transform: uppercase;
                    display: flex; align-items: center; gap: 6px;
                }
                .lsup-field label svg { color: #1d6a88; font-size: 10.5px; }
                .lsup-input-wrap { position: relative; }
                .lsup-field input {
                    width: 100%; padding: 12px 16px;
                    border: 1.5px solid #e0e7ee; border-radius: 10px;
                    font-size: 14px; color: var(--primary-color);
                    background: #f8fafc; outline: none;
                    transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
                    box-sizing: border-box; font-weight: 500; font-family: inherit;
                }
                .lsup-field input:focus {
                    border-color: #1d6a88;
                    box-shadow: 0 0 0 3px rgba(29,106,136,0.1);
                    background: #fff;
                }
                .lsup-field input.has-toggle { padding-right: 44px; }
                .lsup-toggle {
                    position: absolute; right: 13px; top: 50%; transform: translateY(-50%);
                    background: none; border: none; cursor: pointer;
                    color: #b0bec8; font-size: 14px; padding: 0; transition: color 0.2s;
                }
                .lsup-toggle:hover { color: #1d6a88; }

                /* ── Forgot link ── */
                .lsup-forgot-row { display: flex; justify-content: flex-end; margin-top: -8px; }
                .lsup-forgot-link {
                    font-size: 12.5px; font-weight: 600; color: #1d6a88;
                    background: none; border: none; cursor: pointer; padding: 0;
                    transition: color 0.2s; text-decoration: none;
                }
                .lsup-forgot-link:hover { color: var(--primary-color); text-decoration: underline; }

                /* ── Submit ── */
                .lsup-submit-btn {
                    width: 100%; padding: 13px 24px;
                    background: linear-gradient(135deg, #1d6a88, var(--primary-color));
                    color: #fff; border: none; border-radius: 11px;
                    font-size: 15px; font-weight: 700; cursor: pointer;
                    transition: all 0.25s ease;
                    box-shadow: 0 4px 16px rgba(0,37,54,0.22);
                    display: flex; align-items: center; justify-content: center;
                    gap: 10px; margin-top: 4px; font-family: inherit;
                }
                .lsup-submit-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 8px 28px rgba(0,37,54,0.3); }
                .lsup-submit-btn:disabled { opacity: 0.7; cursor: not-allowed; }

                /* ── Switch ── */
                .lsup-switch { text-align: center; margin-top: 20px; font-size: 13.5px; color: #8a99ad; }
                .lsup-switch-btn {
                    background: none; border: none; cursor: pointer;
                    font-size: 13.5px; font-weight: 700; color: #1d6a88;
                    padding: 0; transition: color 0.2s; font-family: inherit;
                }
                .lsup-switch-btn:hover { color: var(--primary-color); text-decoration: underline; }

                /* ── Back btn ── */
                .lsup-back-btn {
                    display: inline-flex; align-items: center; gap: 8px;
                    font-size: 13px; font-weight: 600; color: #1d6a88;
                    background: none; border: none; cursor: pointer;
                    padding: 0 0 20px; transition: color 0.2s; font-family: inherit;
                }
                .lsup-back-btn:hover { color: var(--primary-color); }

                /* ── Success ── */
                .lsup-success { text-align: center; padding: 32px 16px; }
                .lsup-success-icon { font-size: 52px; color: #0e8a5f; margin-bottom: 18px; }
                .lsup-success h3 { font-size: 20px; font-weight: 800; color: var(--primary-color); margin: 0 0 8px; }
                .lsup-success p { font-size: 14px; color: #7a8fa3; margin: 0 0 24px; line-height: 1.6; }

                /* ── Responsive ── */
                @media (max-width: 820px) {
                    .lsup-grid { grid-template-columns: 1fr; max-width: 480px; }
                    .lsup-panel-left { padding: 36px 32px; }
                    .lsup-left-body { margin: 28px 0; }
                    .lsup-left-footer { display: none; }
                }
                @media (max-width: 540px) {
                    .lsup-wrapper { padding: 32px 0 56px; }
                    .lsup-panel-right { padding: 36px 24px; }
                    .lsup-field-row { grid-template-columns: 1fr; }
                    .lsup-grid { border-radius: 18px; }
                }
            `}</style>

            <Header />

            <section className="breadcrumbsection">
                <div className="container">
                    <h1>Login / Signup</h1>
                    <ul className="breadcrumblinks">
                        <li><Link to="/">Home</Link></li>
                        <li><FontAwesomeIcon icon={faChevronRight} /></li>
                        <li>Login / Signup</li>
                    </ul>
                </div>
            </section>

            <div className="lsup-wrapper">
                <div className="container">
                    <div className="lsup-grid">

                        {/* ── Left panel ── */}
                        <div className="lsup-panel-left">
                            <div className="lsup-brand">
                                <h2 className="lsup-brand-logo">Grand Indigo Digitizing</h2>
                                <p className="lsup-brand-tagline">Digitizing · Vectorizing · Branding</p>
                            </div>
                            <div className="lsup-left-body">
                                <h3 className="lsup-left-heading">Your creative work,<br />all in one place.</h3>
                                <p className="lsup-left-sub">
                                    Sign in to manage your orders, request edits, and download invoices instantly.
                                </p>
                                <ul className="lsup-perks">
                                    {PERKS.map((p, i) => (
                                        <li className="lsup-perk" key={i}>
                                            <div className="lsup-perk-icon"><FontAwesomeIcon icon={p.icon} /></div>
                                            <span className="lsup-perk-text">{p.text}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="lsup-left-footer">
                                <p>&copy; {new Date().getFullYear()} Grand Indigo Embroidery</p>
                            </div>
                        </div>

                        {/* ── Right panel ── */}
                        <div className="lsup-panel-right">

                            {/* ════ FORGOT PASSWORD ════ */}
                            {mode === "forgot" && (
                                submitted ? (
                                    <div className="lsup-success">
                                        <div className="lsup-success-icon">
                                            <FontAwesomeIcon icon={faCheckCircle} />
                                        </div>
                                        <h3>Check your inbox</h3>
                                        <p>We've sent a password reset link to your email. It may take a minute to arrive.</p>
                                        <button className="lsup-submit-btn" onClick={() => switchMode("login")}>
                                            Back to Login <FontAwesomeIcon icon={faArrowRight} />
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <button className="lsup-back-btn" onClick={() => switchMode("login")}>
                                            <FontAwesomeIcon icon={faChevronRight} style={{ transform: "rotate(180deg)" }} />
                                            Back to Login
                                        </button>
                                        <h2 className="lsup-form-title">Reset Password</h2>
                                        <p className="lsup-form-subtitle">Enter your email and we'll send you a reset link.</p>

                                        {error && (
                                            <div className="lsup-error-banner">
                                                <FontAwesomeIcon icon={faExclamationCircle} /> {error}
                                            </div>
                                        )}

                                        <div className="lsup-fields">
                                            <div className="lsup-field">
                                                <label><FontAwesomeIcon icon={faEnvelope} /> Email Address</label>
                                                <input
                                                    type="email"
                                                    placeholder="your@email.com"
                                                    value={forgotEmail}
                                                    onChange={e => setForgotEmail(e.target.value)}
                                                    onKeyDown={e => e.key === 'Enter' && handleForgotSubmit()}
                                                />
                                            </div>
                                        </div>
                                        <button className="lsup-submit-btn" onClick={handleForgotSubmit} disabled={loading}>
                                            {loading
                                                ? <><FontAwesomeIcon icon={faSpinner} spin /> Sending...</>
                                                : <>Send Reset Link <FontAwesomeIcon icon={faArrowRight} /></>
                                            }
                                        </button>
                                    </>
                                )
                            )}

                            {/* ════ LOGIN / SIGNUP TABS ════ */}
                            {mode !== "forgot" && (
                                <>
                                    <div className="lsup-tabs">
                                        <button
                                            className={`lsup-tab${mode === "login" ? " active" : ""}`}
                                            onClick={() => switchMode("login")}
                                        >
                                            Sign In
                                        </button>
                                        <button
                                            className={`lsup-tab${mode === "signup" ? " active" : ""}`}
                                            onClick={() => switchMode("signup")}
                                        >
                                            Create Account
                                        </button>
                                    </div>

                                    {/* Error banner — shared for both modes */}
                                    {error && (
                                        <div className="lsup-error-banner">
                                            <FontAwesomeIcon icon={faExclamationCircle} /> {error}
                                        </div>
                                    )}

                                    {/* ── LOGIN ── */}
                                    {mode === "login" && (
                                        <>
                                            <h2 className="lsup-form-title">Welcome back</h2>
                                            <p className="lsup-form-subtitle">Sign in to access your dashboard.</p>

                                            <div className="lsup-fields">
                                                <div className="lsup-field">
                                                    <label><FontAwesomeIcon icon={faEnvelope} /> Email Address</label>
                                                    <input
                                                        type="email"
                                                        placeholder="your@email.com"
                                                        value={loginData.email}
                                                        onChange={e => setLoginData(p => ({ ...p, email: e.target.value }))}
                                                        onKeyDown={e => e.key === 'Enter' && handleLoginSubmit()}
                                                    />
                                                </div>
                                                <div className="lsup-field">
                                                    <label><FontAwesomeIcon icon={faLock} /> Password</label>
                                                    <div className="lsup-input-wrap">
                                                        <input
                                                            type={showPass ? "text" : "password"}
                                                            placeholder="Enter your password"
                                                            className="has-toggle"
                                                            value={loginData.password}
                                                            onChange={e => setLoginData(p => ({ ...p, password: e.target.value }))}
                                                            onKeyDown={e => e.key === 'Enter' && handleLoginSubmit()}
                                                        />
                                                        <button className="lsup-toggle" type="button" onClick={() => setShowPass(v => !v)}>
                                                            <FontAwesomeIcon icon={showPass ? faEyeSlash : faEye} />
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="lsup-forgot-row">
                                                    <button className="lsup-forgot-link" type="button" onClick={() => switchMode("forgot")}>
                                                        Forgot password?
                                                    </button>
                                                </div>
                                            </div>

                                            <button className="lsup-submit-btn" type="button" onClick={handleLoginSubmit} disabled={loading}>
                                                {loading
                                                    ? <><FontAwesomeIcon icon={faSpinner} spin /> Signing in...</>
                                                    : <>Sign In <FontAwesomeIcon icon={faArrowRight} /></>
                                                }
                                            </button>

                                            <p className="lsup-switch">
                                                Don't have an account?{" "}
                                                <button className="lsup-switch-btn" type="button" onClick={() => switchMode("signup")}>
                                                    Create one
                                                </button>
                                            </p>
                                        </>
                                    )}

                                    {/* ── SIGNUP ── */}
                                    {mode === "signup" && (
                                        <>
                                            <h2 className="lsup-form-title">Create account</h2>
                                            <p className="lsup-form-subtitle">Join Grand Indigo and start managing your orders.</p>

                                            <div className="lsup-fields">
                                                <div className="lsup-field-row">
                                                    <div className="lsup-field">
                                                        <label><FontAwesomeIcon icon={faUser} /> First Name</label>
                                                        <input
                                                            type="text" placeholder="John"
                                                            value={signupData.first_name}
                                                            onChange={e => setSignupData(p => ({ ...p, first_name: e.target.value }))}
                                                        />
                                                    </div>
                                                    <div className="lsup-field">
                                                        <label><FontAwesomeIcon icon={faUser} /> Last Name</label>
                                                        <input
                                                            type="text" placeholder="Doe"
                                                            value={signupData.last_name}
                                                            onChange={e => setSignupData(p => ({ ...p, last_name: e.target.value }))}
                                                        />
                                                    </div>
                                                    <div className="lsup-field">
                                                        <label><FontAwesomeIcon icon={faEnvelope} /> Email Address</label>
                                                        <input
                                                            type="email" placeholder="your@email.com"
                                                            value={signupData.email}
                                                            onChange={e => setSignupData(p => ({ ...p, email: e.target.value }))}
                                                        />
                                                    </div>
                                                    <div className="lsup-field">
                                                        <label><FontAwesomeIcon icon={faPhone} /> Phone Number</label>
                                                        <input
                                                            type="tel" placeholder="+1 (555) 000-0000"
                                                            value={signupData.phone}
                                                            onChange={e => setSignupData(p => ({ ...p, phone: e.target.value }))}
                                                        />
                                                    </div>
                                                    <div className="lsup-field">
                                                        <label><FontAwesomeIcon icon={faLock} /> Password</label>
                                                        <div className="lsup-input-wrap">
                                                            <input
                                                                type={showPass ? "text" : "password"}
                                                                placeholder="Create password"
                                                                className="has-toggle"
                                                                value={signupData.password}
                                                                onChange={e => setSignupData(p => ({ ...p, password: e.target.value }))}
                                                            />
                                                            <button className="lsup-toggle" type="button" onClick={() => setShowPass(v => !v)}>
                                                                <FontAwesomeIcon icon={showPass ? faEyeSlash : faEye} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <div className="lsup-field">
                                                        <label><FontAwesomeIcon icon={faLock} /> Confirm Password</label>
                                                        <div className="lsup-input-wrap">
                                                            <input
                                                                type={showConfirm ? "text" : "password"}
                                                                placeholder="Confirm password"
                                                                className="has-toggle"
                                                                value={signupData.password_confirmation}
                                                                onChange={e => setSignupData(p => ({ ...p, password_confirmation: e.target.value }))}
                                                            />
                                                            <button className="lsup-toggle" type="button" onClick={() => setShowConfirm(v => !v)}>
                                                                <FontAwesomeIcon icon={showConfirm ? faEyeSlash : faEye} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <button className="lsup-submit-btn" type="button" onClick={handleSignupSubmit} disabled={loading}>
                                                {loading
                                                    ? <><FontAwesomeIcon icon={faSpinner} spin /> Creating account...</>
                                                    : <>Create Account <FontAwesomeIcon icon={faArrowRight} /></>
                                                }
                                            </button>

                                            <p className="lsup-switch">
                                                Already have an account?{" "}
                                                <button className="lsup-switch-btn" type="button" onClick={() => switchMode("login")}>
                                                    Sign in
                                                </button>
                                            </p>
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <Newsletter />
            <Footer />
        </>
    );
}